<script setup>

    import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
    import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/vue/20/solid'
    import LwxLotus from '@/Components/LwxLotus.vue'

    import { ref, computed, watch } from 'vue'
    import { useAuthStore, useStaffStore } from '@/Stores'
    import { storeToRefs } from 'pinia'

    import { getModifiedProperties } from '@/Helpers'

    const staffStore = useStaffStore()
    const authStore = useAuthStore()

    const { allStaffs } = storeToRefs(staffStore)
    const { user, userWithId } = storeToRefs(authStore)

    const { staff, loading, variant, filter, showClearButton } = defineProps({
        loading: {
            type: Boolean,
            default: false
        },
        staff: {
            type: Object,
            required: true
        },
        variant: {
            type: String,
            default: 'alabaster' // alabaster, container
        },
        filter: {
            type: String,
            default: 'all' // all, activeStaffsWithClients
        },
        showClearButton: {
            type: Boolean,
            default: false
        }
    })

    const filteredStaffs = computed(() => {
        const staffs = filter === 'activeStaffsWithClients' ? staffStore.activeStaffsWithClients : allStaffs.value.filter(staffElement => staffElement.is_active || staffElement.id == staff.id || staffElement.id == user.value.user_id)

        staffs.sort((a, b) => a.first_name.localeCompare(b.first_name))
        return staffs;
    })

    const selected = ref(staff)

    function resetSelectedToUser() {
        selected.value = allStaffs.value.find(filteredStaff => filteredStaff.id == user.value.user_id)
    }

    const emit = defineEmits(['assigned-staff:update'])

    watch(() => selected.value, (newValue, oldValue) => {
        if (oldValue !== undefined && newValue !== undefined) {
            const modifiedData = getModifiedProperties(
                JSON.stringify(newValue),
                JSON.stringify(oldValue),
                [
                    'coffee_auth_user_id', 'email', 'first_name', 'id', 'is_active', 'last_name'
                ]
            )
            if (modifiedData && Object.keys(modifiedData).length > 0) {
                emit('assigned-staff:update', newValue);
            }
        }
    }, { immediate: true })

    watch(() => filteredStaffs.value, () => {
        if (staff.id) {
            selected.value = filteredStaffs.value.find(filteredStaff => filteredStaff.id == staff.id)
        } else {
            selected.value = filteredStaffs.value.find(filteredStaff => filteredStaff.id == user.value.user_id)
        }
    }, { immediate: true })

    function buttonClasses() {
        let baseClasses = 'relative w-full cursor-default rounded-md py-1.5 pl-2.5 pr-9 md:pl-3 md:pr-10 text-left text-xs md:text-sm font-semibold ring-1 ring-inset focus:outline-none focus:ring-2 leading-5 md:leading-6 ';
        if (variant === 'alabaster') {
            baseClasses += 'bg-lifeworx-alabaster-200/50 text-lifeworx-blue-700 ring-stone-300 focus:ring-lifeworx-blue-600 ';
        } else if (variant === 'container') {
            baseClasses += 'md:min-w-60 bg-lifeworx-gunmetal-800/50 text-white ring-lifeworx-blue-600 focus:ring-lifeworx-blue-500 hover:ring-lifeworx-blue-500';
        }
        return baseClasses;
    }

</script>

<template>

    <div class="flex w-full min-w-0 items-center gap-2 md:min-w-60">
        <Listbox
            as="div"
            v-model="selected"
            class="w-full min-w-0"
        >
        <div class="relative w-full">
            <ListboxButton :class="buttonClasses()">
                <template v-if="filteredStaffs.length == 0">
                    <span class="flex items-center">
                        <span class="truncate text-xs md:text-sm">Loading...</span>
                    </span>
                </template>
                <template v-else>
                    <span class="flex items-center">
                        <LwxLotus class="inline-flex h-3.5 md:h-4 text-lifeworx-blue-600" />
                        <span class="ml-2 md:ml-3 block truncate text-xs md:text-sm">{{ selected.first_name + ' ' + selected.last_name }}</span>
                    </span>
                </template>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon class="h-4 w-4 md:h-5 md:w-5 text-gray-400" aria-hidden="true" />
            </span>
            </ListboxButton>

            <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions
                :class="[
                    'absolute z-popover mt-1 max-h-96 w-full overflow-auto rounded-md py-1 text-xs md:text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                    {
                        'bg-lifeworx-alabaster-200': variant === 'alabaster',
                        'bg-white': variant === 'container'
                    }
                ]">
                <ListboxOption as="template" v-for="staff in filteredStaffs" :key="staff.id" :value="staff" v-slot="{ active, selected }">
                <li :class="[active ? 'bg-lifeworx-blue-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-1.5 md:py-2 pl-3 pr-9']">
                    <div class="flex items-center">
                    <span :class="[selected ? active ? 'font-semibold text-white' : 'font-semibold text-lifeworx-blue-600' : 'font-normal', 'ml-2 md:ml-3 block truncate']">
                        {{ staff.first_name + ' ' + staff.last_name }}
                    </span>
                    </div>

                    <span v-if="selected" class="absolute inset-y-0 right-0 flex items-center pr-4">
                    <CheckIcon
                        :class="[active ? 'text-white' : 'text-lifeworx-blue-600', 'h-5 w-5']"
                        aria-hidden="true" />
                    </span>
                </li>
                </ListboxOption>
            </ListboxOptions>
            </transition>
        </div>
        </Listbox>

        <button
            v-if="showClearButton && selected && selected.id && user && selected.id !== userWithId.id"
            @click="resetSelectedToUser()"
            class="h-8 w-8 mt-0.5 md:h-7 md:w-7 p-1 grow-0 justify-self-end items-center justify-center rounded-md text-white hover:bg-lifeworx-blue-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-lifeworx-blue-700"
        >
            <span class="sr-only">Clear</span>
            <XMarkIcon class="block h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
        </button>

    </div>

  </template>
